import { createElement as rc, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { View, h4, fromTheme, styled, ScrollView } from 'lib_ui-primitives';
import useFormControl from '../../hooks/useFormControl';
import DropDownButton from '../_abstractComponent/DropDownButton';
import { WorkflowActionPane } from '../workflowPane';
import { metadata } from 'lib_ui-services';
import createTreePositionGetter from '../../utilities/createTreePositionGetter';

const StyledActionList = styled(View).attrs({ name: 'element-groups-container' })`
    width: 100%;
    background-color: ${fromTheme('backgroundColor')};
    flex-direction: column;
    padding: ${fromTheme('viewPadding')};
    margin-top: ${fromTheme('viewMargin')};
`;

const Header = styled(View).attrs({ name: 'header' })`
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    min-height: 45px;
`;

const ElementGroup = styled(WorkflowActionPane).attrs({ name: 'element-group' })``;

export const _private = {
    useFormControl
};

function OptionGroups(props) {
    const {
        hNode: { id, propertyName, propertyPath: parentPropertyPath, children: fieldTemplates, treePosition },
        currentRoute
    } = props;

    const { title, value, setValue, disabled } = _private.useFormControl(props);
    const [usedTemplates, setUsedTemplates] = useState([]);

    const children = useMemo(() => {
        function remove(index, entryType) {
            setValue(prev => {
                return prev.filter((value, i) => i !== index);
            });
            setUsedTemplates(prev => {
                return prev.filter(template => template !== entryType);
            });
        }

        const getTreePosition = createTreePositionGetter(treePosition, value.length);

        function fromDefinition(fieldTemplates, entry, index) {
            const template = fieldTemplates.find(template => typeFromHNode(template) === entry.type);
            if (template) {
                setUsedTemplates(prev => {
                    const newTemplates = [...prev, entry.type];
                    return [...new Set(newTemplates)];
                });

                const propertyPath = `${metadata.getPathToProperty({
                    propertyPath: parentPropertyPath,
                    propertyName
                })}[${index}]`;

                return rc(ElementGroup, {
                    hNode: {
                        ...injectPropertyPath(template, propertyPath),
                        treePosition: getTreePosition(index)
                    },
                    key: index,
                    childIndex: index,
                    value: entry,
                    onRemove: () => remove(index, entry.type),
                    disabled,
                    currentRoute
                });
            }
            return null;
        }

        return value.map((entry, index) => fromDefinition(fieldTemplates, entry, index));
    }, [treePosition, value, setValue, parentPropertyPath, propertyName, disabled, currentRoute, fieldTemplates]);

    const templates = useMemo(() => {
        function add(newEntry) {
            setValue(prev => {
                return [...prev, newEntry];
            });
        }

        return fieldTemplates.map((hNode, i) => {
            const { title } = hNode || {};
            const action = typeFromHNode(hNode);
            let defaultEntry = applyDefaults(hNode, {
                _displayName: title
            });
            if (!defaultEntry.type) {
                defaultEntry.type = action;
            }

            return rc(DropDownButton.MenuItem, {
                hNode: { title },
                disabled: usedTemplates.includes(action),
                onClick: () => add(defaultEntry),
                key: action + i
            });
        });
    }, [fieldTemplates, setValue, usedTemplates]);

    // prettier-ignore
    return rc(StyledActionList, { id },
        rc(Header, null,
            rc(h4, null, title),
            rc(DropDownButton, { id, icon: 'add', disabled, color: 'success' },
                templates
            )
        ),
        rc(ScrollView, null,
            children
        )
    );
}

OptionGroups.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};
export default OptionGroups;

const typeFromHNode = (hNode = {}) => {
    // 2 options to identify the type of the option
    // 1. A child component with a "type" propertyName and a default value
    const typeChild = getTypeChild(hNode);
    if (typeChild?.defaultValue) {
        return typeChild.defaultValue;
    }

    // 2. either the title, or the node's propertyName itself
    return hNode.title || hNode.propertyName;
};

const getTypeChild = (hNode = {}) => {
    if (hNode.propertyName === 'type') {
        return hNode;
    }
    if (hNode.children) {
        return hNode.children.reduce((acc, child) => acc || getTypeChild(child), undefined);
    }
};

const applyDefaults = (hNode, value) => {
    // simple recursive function to set default values
    // does NOT support "default to previous selection" filter
    // does not support nested objects
    return (
        hNode?.children?.reduce((acc, child) => {
            const { propertyName, defaultValue } = child;
            if (propertyName && defaultValue && typeof acc[propertyName] === 'undefined') {
                return { ...acc, [propertyName]: defaultValue };
            } else if (child.hNodeTypeGroup !== 'formElement' && child.children?.length) {
                return applyDefaults(child, acc);
            } else return acc;
        }, value) || value
    );
};

const injectPropertyPath = (hNode, propertyPath) => {
    // add the propertyPath to each formElement.
    // if not, recurse into the children and add the propertyPath to each child.
    if (hNode.hNodeTypeGroup === 'formElement') {
        return { ...hNode, propertyPath };
    } else if (hNode.children) {
        return { ...hNode, children: hNode.children.map(child => injectPropertyPath(child, propertyPath)) };
    }
    return hNode;
};
